import { type WorkflowNodeType } from '@blaze-shared/api-contracts-domain';
import { getDownstreamNodes, getParentCluster, type Workflow } from '@blaze-shared/workflow-domain';

/** What the debugger can be asked to do next from a given node. */
export enum DebugNodeActions {
  STEP_OVER = 'STEP_OVER',
  STEP_INTO = 'STEP_INTO',
  STEP_OUTSIDE = 'STEP_OUTSIDE',
  RUN_TO_NODE = 'RUN_TO_NODE',
}

/** Node types that contain a body, and so can be stepped into. */
const NODE_TYPES_WITH_INSIDE_STEPS: WorkflowNodeType[] = ['ForEach', 'While'];

/**
 * The debugger actions offered on `nodeId` while execution is paused at `pendingNodeId`: stepping
 * choices on the paused node itself, and "run to here" on any node still ahead of it.
 */
export const calculateAllowedActionsForNode = (
  nodeId: string,
  workflow: Workflow,
  pendingNodeId: string | undefined,
): DebugNodeActions[] => {
  const isPendingNode = nodeId === pendingNodeId;

  const allNodes = workflow.nodes;
  const node = allNodes.find((node) => node.id === nodeId);
  if (!node) {
    return [];
  }

  const actions: DebugNodeActions[] = [];

  if (isPendingNode) {
    actions.push(DebugNodeActions.STEP_OVER);

    const isClusterStep = NODE_TYPES_WITH_INSIDE_STEPS.includes(node.nodeType);
    if (isClusterStep) {
      actions.push(DebugNodeActions.STEP_INTO);
    }
    const containingClusterNode = getParentCluster(node.id, allNodes);
    if (containingClusterNode) {
      actions.push(DebugNodeActions.STEP_OUTSIDE);
    }
    return actions;
  }

  const nodesInExecutionQueue = getDownstreamNodes(pendingNodeId, workflow);
  const isNotExecutedYet = nodesInExecutionQueue.some(({ id }) => id === nodeId);
  if (isNotExecutedYet && !isPendingNode) {
    actions.push(DebugNodeActions.RUN_TO_NODE);
  }

  return actions;
};
